import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import profileClientIcon from '../../assets/img3d/profileClient.png';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Eye, Clock, CheckCircle, XCircle, Calendar, 
  Mail, Phone, Home, Filter, Search 
} from 'lucide-react';
import axios from 'axios';
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalLeadsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // On utilise 'filter' pour gérer l'état local du filtre actif (correspondant aux onglets)
  const [filter, setFilter] = useState('all'); 
  const propertyId = searchParams.get('propertyId');

  useEffect(() => {
    fetchLeads();
  }, [filter]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Mapping des filtres UI vers les paramètres API
      if (filter === 'pending') params.append('professionalStatus', 'PENDING');
      else if (filter === 'accepted') params.append('professionalStatus', 'ACCEPTED');
      else if (filter === 'refused') params.append('professionalStatus', 'REFUSED');

      const response = await axios.get(`${API}/professional/leads?${params.toString()}`);
      const leadsData = response.data;
      setLeads(Array.isArray(leadsData) ? leadsData : (leadsData?.items || []));
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = (status || '').toString().trim().toUpperCase();
    const configs = {
      NEW: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Nouveau', icon: Clock },
      ASSIGNED: { color: 'bg-[#FF9F68]/10 text-orange-700 border-orange-200', label: 'Assigné', icon: Clock },
      PENDING: { color: 'bg-[#FF9F68]/10 text-orange-700 border-orange-200', label: 'En attente', icon: Clock },
      ACCEPTED: { color: 'bg-green-50 text-green-700 border-green-200', label: 'Accepté', icon: CheckCircle },
      REFUSED: { color: 'bg-red-50 text-red-700 border-red-200', label: 'Refusé', icon: XCircle },
    };
    const config = configs[normalizedStatus] || configs.NEW;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={cn("border px-2 py-0.5 text-xs font-medium flex items-center gap-1.5", config.color)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', { 
        day: 'numeric', month: 'short'
      });
    } catch { return ''; }
  };

  // Les pros ne voient pas les demandes au statut NEW (réservé admin)
  const visibleLeads = useMemo(() => {
    if (!Array.isArray(leads)) return [];
    return leads.filter((lead) => {
      const status = (lead.status || '').toString().trim().toUpperCase();
      return status !== 'NEW';
    });
  }, [leads]);

  // Filtrage local supplémentaire si propertyId est présent
  const filteredLeads = useMemo(() => {
    if (!visibleLeads || !Array.isArray(visibleLeads)) return [];
    if (propertyId) {
      return visibleLeads.filter((l) => l.propertyId === propertyId || l.property?.id === propertyId);
    }
    return visibleLeads;
  }, [visibleLeads, propertyId]);

  const filtersList = [
    { id: 'all', label: 'Tous' },
    { id: 'pending', label: 'En attente' },
    { id: 'accepted', label: 'Acceptés' },
    { id: 'refused', label: 'Refusés' }
  ];

  return (
    <Layout
      headerConfig={{
        icon: profileClientIcon,
        iconColor: 'bg-white',
        title: 'Gestion des Leads',
        subtitle: 'Suivez et qualifiez vos opportunités',
        primaryAction: 'Tableau de Bord',
        onPrimaryAction: () => navigate('/professional'),
      }}
    >
      <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">

        {/* --- BARRE DE FILTRES --- */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-gray-500 mr-2">
              <Filter className="h-4 w-4" />
              <span>Statut :</span>
            </div>
            {filtersList.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border",
                  filter === f.id
                    ? "bg-[#FF9F68] text-white border-[#FF9F68] shadow-sm shadow-orange-100"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Badge Filtre par Bien (si actif) */}
          {propertyId && (
            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 px-3 py-1.5 flex items-center gap-2">
              <Home className="h-3 w-3" />
              Filtré par bien
              <button
                onClick={() => navigate('/professional/leads')}
                className="ml-1 hover:bg-blue-100 rounded-full p-0.5 transition-colors"
              >
                <XCircle className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>

        {/* --- LISTE DES LEADS --- */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse" />)}
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
              <Users className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Aucun lead trouvé</h3>
            <p className="text-gray-500">Essayez de modifier vos filtres.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredLeads.map((lead) => {
              const seekerName = lead.seekerName || lead.seeker_name;
              const seekerEmail = lead.seekerEmail || lead.seeker_email;
              const seekerPhone = lead.seekerPhone || lead.seeker_phone;
              const seekerMessage = lead.seekerMessage || lead.seeker_message;
              const createdAt = lead.createdAt || lead.created_at;
              const visitDate = lead.visitDate || lead.visit_date;
              const property = lead.property;

              return (
                <Card 
                  key={lead.id} 
                  className="group overflow-hidden rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => navigate(`/professional/leads/${lead.id}`)}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      
                      {/* Partie Gauche : Infos Lead */}
                      <div className="flex-1 p-6">
                        <div className="flex justify-between items-start mb-4">
                        <div className="flex flex-col">
                          <h3 className="font-bold text-gray-900 text-lg leading-tight">{seekerName || 'Lead Inconnu'}</h3>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Clock className="h-3 w-3" /> Reçu le {formatDate(createdAt)}
                          </div>
                          </div>
                          {getStatusBadge(lead.status)}
                        </div>

                        {/* Contact Rapide (Email/Tel) */}
                        <div className="flex flex-wrap gap-4 mb-4">
                          {seekerEmail && (
                            <a href={`mailto:${seekerEmail}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#FF9F68] transition-colors">
                              <Mail className="h-4 w-4" /> {seekerEmail}
                            </a>
                          )}
                          {seekerPhone && (
                            <a href={`tel:${seekerPhone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#FF9F68] transition-colors">
                              <Phone className="h-4 w-4" /> {seekerPhone}
                            </a>
                          )}
                        </div>

                        {/* Extrait Message */}
                        {seekerMessage && (
                          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600 italic border-l-2 border-gray-300">
                            "{seekerMessage}"
                          </div>
                        )}
                      </div>

                      {/* Partie Droite : Contexte Bien & Visite (Fond grisé léger) */}
                      <div className="md:w-72 bg-gray-50/50 border-l border-gray-100 p-6 flex flex-col justify-between">
                        <div>
                          {property && (
                            <div className="mb-4">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Bien concerné</p>
                              <div className="flex items-start gap-2">
                                <Home className="h-4 w-4 text-[#FF9F68] mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900 line-clamp-1" title={property.title}>{property.title}</p>
                                  {property.location && <p className="text-xs text-gray-500 mt-0.5">{property.city}</p>}
                                </div>
                              </div>
                            </div>
                          )}
                          {visitDate && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Visite</p>
                              <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 px-2 py-1 rounded-md border border-purple-100 inline-flex">
                                <Calendar className="h-3.5 w-3.5" />
                                {formatDate(visitDate)}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-4 md:mt-0 flex justify-end">
                          <Button 
                            size="sm" 
                            className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white shadow-sm"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Détails
                          </Button>
                        </div>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfessionalLeadsPage;